/**
 * Page Manager
 * Manages multi-page form navigation and state
 */

import { ErrorManager } from '../validation/error_manager.js';

export class PageManager {
  constructor(validator, errorManager = null) {
    this.validator = validator;
    this.errorManager = errorManager;
    this.currentPage = 1;
    this.totalPages = 0;
  }

  /**
   * Initialize pages
   * @param {Array} pages - Pages array from form config
   */
  initialize(pages) {
    if (!pages || !Array.isArray(pages)) {
      this.totalPages = 1;
      return;
    }

    this.totalPages = pages.length;
    
    // Sort pages by position
    const sortedPages = [...pages].sort((a, b) => {
      const posA = a.poisition || a.position || 0;
      const posB = b.poisition || b.position || 0;
      return posA - posB;
    });

    // Show first page, hide others
    sortedPages.forEach((page, index) => {
      const pageNum = page.poisition || page.position || (index + 1);
      const pageElement = document.querySelector(`.page[data-num="${pageNum}"]`);
      
      if (pageElement) {
        if (pageNum === 1) {
          pageElement.classList.remove('d-none');
        } else {
          pageElement.classList.add('d-none');
        }
      }
    });

    this.currentPage = 1;
  }

  /**
   * Get current page number
   * @returns {number}
   */
  getCurrentPage() {
    return this.currentPage;
  }

  /**
   * Get total pages
   * @returns {number}
   */
  getTotalPages() {
    return this.totalPages;
  }

  /**
   * Navigate to page
   * @param {number} pageNum - Page number
   * @param {boolean} validate - Whether to validate before navigation
   * @returns {boolean} Success
   */
  navigateToPage(pageNum, validate = true) {
    if (pageNum < 1 || pageNum > this.totalPages) {
      return false;
    }

    // Validate current page before navigating forward
    if (validate && pageNum > this.currentPage) {
      if (!this.validateCurrentPage()) {
        return false;
      }
    }

    // Hide all pages
    const allPages = document.querySelectorAll('.page');
    allPages.forEach(page => {
      page.classList.add('d-none');
    });

    // Show target page
    const targetPage = document.querySelector(`.page[data-num="${pageNum}"]`);
    if (targetPage) {
      targetPage.classList.remove('d-none');
    }

    // Update page items
    const allPageItems = document.querySelectorAll('.page-item');
    allPageItems.forEach(item => {
      item.classList.remove('active');
    });

    const targetPageItem = document.querySelector(`.page-item[data-num="${pageNum}"]`);
    if (targetPageItem) {
      targetPageItem.classList.add('active');
    }

    this.currentPage = pageNum;
    window.scrollTo(0, 0);

    if (typeof this.onPageChange === 'function') {
      this.onPageChange();
    }
    return true;
  }

  /**
   * Navigate to next page
   * @returns {boolean} Success
   */
  nextPage() {
    if (this.currentPage >= this.totalPages) {
      return false;
    }

    return this.navigateToPage(this.currentPage + 1, true);
  }

  /**
   * Navigate to previous page
   * @returns {boolean} Success
   */
  previousPage() {
    if (this.currentPage <= 1) {
      return false;
    }

    return this.navigateToPage(this.currentPage - 1, false);
  }

  /**
   * Get signature validation for the active page only (DOM-based: sign_me / signature_placeholder).
   * @returns {{ isValid: boolean, errors: Array<{ fieldId: string, message: string }> }}
   */
  getSignatureValidationOnCurrentPage() {
    const currentPageElement = document.querySelector(`.page[data-num="${this.currentPage}"]`);
    if (!currentPageElement) {
      return { isValid: true, errors: [] };
    }

    const errors = [];
    const formGroups = currentPageElement.querySelectorAll('.form-group');

    formGroups.forEach(group => {
      const signButton = group.querySelector('.sign_me');
      const signatureInput = group.querySelector('input.signature_placeholder');
      if (!signButton && !signatureInput) return;

      const fieldId = signButton?.getAttribute('data-id') || signatureInput?.name || signatureInput?.id;
      if (!fieldId) return;

      const isVisible = () => {
        const style = window.getComputedStyle(group);
        return style.display !== 'none' && style.visibility !== 'hidden' && !group.closest('.d-none');
      };
      if (!isVisible()) return;

      const input = group.querySelector(`[name="${fieldId}"]`) || signatureInput;
      const signed = (input && String(input.value || '').trim() !== '') || (signButton && signButton.classList.contains('d-none'));
      if (!signed) {
        const label = group.querySelector('span')?.textContent?.trim() || 'Signature field';
        errors.push({ fieldId, message: `Please sign in ${label}` });
      }
    });

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Validate current page
   * @returns {boolean}
   */
  validateCurrentPage() {
    const currentPageElement = document.querySelector(`.page[data-num="${this.currentPage}"]`);

    if (this.validator) {
      // Mark all fields on current page as touched before validation
      if (currentPageElement) {
        const fields = currentPageElement.querySelectorAll('input, select, textarea');
        fields.forEach(field => {
          const fieldId = field.name || field.id;
          if (fieldId) {
            field.classList.add('touched');
          }
        });
      }
      if (!this.validator.validatePage(this.currentPage)) {
        return false;
      }
    }

    const sigResult = this.getSignatureValidationOnCurrentPage();
    if (!sigResult.isValid && currentPageElement && this.errorManager) {
      sigResult.errors.forEach(({ fieldId, message }) => {
        const field = currentPageElement.querySelector(`[name="${fieldId}"]`) || currentPageElement.querySelector(`.sign_me[data-id="${fieldId}"]`);
        if (field) {
          ErrorManager.showError(field, message);
        }
      });
      return false;
    }

    return true;
  }

  /**
   * Check if signatures are valid on current page
   * @param {Array} signaturePads - Array of signature pad instances
   * @returns {Object}
   */
  validatePageSignatures(signaturePads) {
    const currentPageElement = document.querySelector(`.page[data-num="${this.currentPage}"]`);
    if (!currentPageElement) {
      return { isValid: true, errors: [] };
    }

    const signatureCanvases = currentPageElement.querySelectorAll('canvas.signature:not(.d-none)');
    const errors = [];

    signatureCanvases.forEach(canvas => {
      const signaturePadId = canvas.getAttribute('id');
      const signatureObject = signaturePads.find(pad => pad._canvas?.id === signaturePadId);

      if (signatureObject && signatureObject.isEmpty()) {
        const fieldContainer = canvas.closest('.form-group');
        const label = fieldContainer?.querySelector('span')?.textContent || 'Signature field';
        errors.push({
          field: signaturePadId,
          message: `You did not sign in field ${label}`
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

